import React from "react";
import Loader from "components/common/Loader/Loader";
import ToggleSwitch from "components/common/NewSwitch";
import {
  TimesheetDetailCard,
  TimesheetFieldLabel,
  TimesheetParticipantRow,
  TimesheetTimeToDivider,
} from "./TimesheetTabPrimitives";

/** Section title for time blocks */
export const TimesheetTimeSection = ({
  title,
  startLabel,
  endLabel,
  startInput,
  endInput,
  startError,
  endError,
  durationText,
  durationWarning = false,
}) => (
  <TimesheetDetailCard className="td-grid-cell">
    <p className="td-section-title">{title}</p>
    <div className="td-time-block-row">
      <div className="td-time-slot">
        {typeof startLabel === "string" ? (
          <span className="td-time-slot-label">{startLabel}</span>
        ) : (
          startLabel
        )}
        {startInput}
        {startError}
      </div>
      <TimesheetTimeToDivider />
      <div className="td-time-slot">
        {typeof endLabel === "string" ? (
          <span className="td-time-slot-label">{endLabel}</span>
        ) : (
          endLabel
        )}
        {endInput}
        {endError}
      </div>
    </div>
    {durationText && (
      <p
        className={`td-duration-hint ${durationWarning ? "is-warning" : ""}`}
      >
        {durationText}
      </p>
    )}
  </TimesheetDetailCard>
);

export const TimesheetCompliancePanel = ({ incidentField, incidentError }) => (
  <TimesheetDetailCard className="td-grid-cell td-compliance-card">
    <TimesheetFieldLabel required>Incident details</TimesheetFieldLabel>
    {incidentField}
    {incidentError}
  </TimesheetDetailCard>
);

export const TimesheetCrossOverToggleRow = ({ value, onChange, disabled }) => (
  <div className="td-crossover-bar">
    <span className="td-toggle-row-label">Cross over rule</span>
    <ToggleSwitch
      value={value === true || value === 1 || value === "1"}
      isSmall
      disabled={disabled}
      onChange={onChange}
    />
  </div>
);

/**
 * Shift-level sleepover + active hours on timesheet details
 * (basic, basic split, and advance when shift sleepover is on).
 */
export const TimesheetSleepoverActiveHoursRow = ({
  sleepover,
  onSleepoverChange,
  activeHours,
  onActiveHoursChange,
  disabled,
}) => {
  const sleepoverOn =
    sleepover === true || sleepover === 1 || sleepover === "1";

  return (
    <TimesheetDetailCard
      className={`td-sleepover-card ${sleepoverOn ? "is-active" : ""}`}
    >
      <div className="td-sleepover-header">
        <div className="td-sleepover-copy">
          <p className="td-section-title">Sleepover</p>
          <p className="td-sleepover-subtitle">
            This shift is marked as sleepover. Keep the toggle on and enter
            active hours if needed (HH:MM).
          </p>
        </div>
        <div className="td-sleepover-control">
          <span
            className={`td-sleepover-status ${sleepoverOn ? "is-on" : "is-off"}`}
          >
            {sleepoverOn ? "On" : "Off"}
          </span>
          <ToggleSwitch
            value={sleepoverOn}
            isSmall
            disabled={disabled}
            onChange={onSleepoverChange}
          />
        </div>
      </div>

      {sleepoverOn && (
        <div className="td-active-hours-panel">
          <div className="td-active-hours-copy">
            <TimesheetFieldLabel>Active hours</TimesheetFieldLabel>
            <p className="td-active-hours-hint">
              Optional. Active time during the sleepover (HH:MM).
            </p>
          </div>
          <div className="td-active-hours-input-wrap">
            <input
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              disabled={disabled}
              value={activeHours || ""}
              onChange={(e) => onActiveHoursChange?.(e.target.value)}
              className="td-active-hours-input"
              aria-label="Active hours"
            />
            <span className="td-active-hours-suffix" aria-hidden>
              HH:MM
            </span>
          </div>
        </div>
      )}
    </TimesheetDetailCard>
  );
};

export const TimesheetCostsPanel = ({ costField, costError, kmField, kmError }) => (
  <TimesheetDetailCard className="td-grid-cell td-costs-card">
    <div className="td-costs-stack">
      <div className="min-w-0">
        <TimesheetFieldLabel>Shift associated cost $</TimesheetFieldLabel>
        {costField}
        {costError}
      </div>
      <div className="min-w-0">
        <TimesheetFieldLabel>Acceptable KM</TimesheetFieldLabel>
        {kmField}
        {kmError}
      </div>
    </div>
  </TimesheetDetailCard>
);

export const TimesheetParticipantNotesPanel = ({
  participant,
  participantCount,
  notesLabel,
  notesRequired = false,
  notesField,
  notesError,
  instructionsLabel,
  instructionsField,
  showInstructions,
}) => (
  <TimesheetDetailCard className="td-participant-panel td-participant-panel--full">
    {participant && (
      <div className="td-participant-header">
        <TimesheetParticipantRow
          participant={participant}
          participantCount={participantCount}
          compact
        />
      </div>
    )}
    <div
      className={`td-notes-grid ${
        showInstructions ? "" : "td-notes-grid--single"
      }`}
    >
      <div className="min-w-0 w-full">
        <TimesheetFieldLabel required={notesRequired}>{notesLabel}</TimesheetFieldLabel>
        {notesField}
        {notesError}
      </div>
      {showInstructions && (
        <div className="min-w-0 w-full">
          <TimesheetFieldLabel>{instructionsLabel}</TimesheetFieldLabel>
          {instructionsField}
        </div>
      )}
    </div>
  </TimesheetDetailCard>
);

const TimesheetSaveIcon = () => (
  <svg
    className="h-4 w-4 shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15.2 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.8L15.2 3z" />
    <path d="M14 3v4h4" />
    <path d="M8 13h8" />
    <path d="M8 17h5" />
  </svg>
);

export const TimesheetDetailsSaveBar = ({ loading, disabled, className = "" }) => (
  <div className={`td-save-bar ${className}`.trim()}>
    <button
      type="submit"
      disabled={disabled || loading}
      className="td-save-btn"
    >
      {loading ? (
        <Loader cssClass="!mr-0 h-4 w-4" isSmallView />
      ) : (
        <TimesheetSaveIcon />
      )}
      Save
    </button>
  </div>
);
